import { css } from 'styled-components';

/**
 * The treatment for text the panel did not write.
 *
 * Every string that reaches a table cell, a detail row or a dropdown option
 * came from a person — a ticket description, an order note, a cancellation
 * reason, a category title — and this product is used in Saudi Arabia, so any
 * of them may be Arabic, Latin, or both in one sentence. Nothing in the
 * payload says which.
 *
 * Two problems follow, and both are solved without asking what language the
 * string is in:
 *
 * **Direction.** `unicode-bidi: plaintext` tells the browser to take each
 * paragraph's base direction from its own first strong character rather than
 * from the page. An Arabic note then reads right-to-left and an English one
 * left-to-right, with no language detection, no `dir` attribute to set at
 * every call site, and no guessing. It is the standard answer for text of
 * unknown language and it is why the property exists.
 *
 * Before this, an Arabic value sat inside an LTR paragraph: its words were in
 * the right order but its punctuation moved to the wrong end, and a value
 * mixing Arabic with a Latin word or a number came out genuinely scrambled.
 *
 * **The face.** `--font-content` is Plex Sans followed by Plex Sans Arabic.
 * Font fallback is per glyph, so Latin is set in Plex Sans and Arabic in Plex
 * Sans Arabic out of one declaration — including inside a single mixed
 * string. Until now Arabic content anywhere outside an `ar_*` input fell
 * through to whatever the operating system happened to have.
 *
 * This is deliberately **not** `--font-ar`, and deliberately does not set
 * `text-align`. `--font-ar` puts the Arabic face first, which is right for a
 * field declared Arabic and wrong for a column that is mostly English. And a
 * table column has to keep one alignment or its edge zig-zags down the page;
 * ordering is what was broken, not alignment. Detail rows, which are one
 * value each and not a column, do take `text-align: start` — see `bidiProse`.
 *
 * `unicode-bidi` is not inherited, so it goes on the element that directly
 * contains the text. The call sites apply it to the container *and* its
 * immediate child, because a cell renders either a bare value or one
 * wrapping div — the same shape AppTable's ellipsis rule already handles.
 */
export const bidiText = css`
  font-family: var(--font-content);
  unicode-bidi: plaintext;
`;

/**
 * `bidiText` plus alignment, for a value that owns its line rather than a
 * column: a detail row, an empty state's message.
 *
 * `text-align: start` resolves against the direction `plaintext` worked out,
 * so an Arabic paragraph aligns right and an English one left, from one
 * declaration.
 */
export const bidiProse = css`
  ${bidiText};
  text-align: start;
`;
